// Import necessary dependencies
import {
  DisconnectButton,
  RoomAudioRenderer,
  useVoiceAssistant,
  BarVisualizer,
  RoomContext,
  VoiceAssistantControlBar,
  AgentState,
} from "@livekit/components-react";
import { CloseIcon } from "./components/CloseIcon";
import { NoAgentNotification } from "./components/NoAgentNotification";
// Import Krisp noise filter
import { useKrispNoiseFilter } from "@livekit/components-react/krisp";
// Import LiveKit styles correctly
import "@livekit/components-styles";
// Import custom styles for the agent
import "./globals.css";
import { AnimatePresence, motion } from "framer-motion";
import { MediaDeviceFailure, Track } from "livekit-client";
import { useCallback, useEffect, useState } from "react";
import { LIVEKIT_TOKEN_ENDPOINT, MODAL_AGENT_ENDPOINT } from "./config";
import { ConversationScenarioSelector } from "./components/ConversationScenarioSelector";
import { Room, RoomEvent } from "livekit-client";
import { base64ToBlob } from "../utils/audioUtils";
import { startCallBase64 } from "./sounds/startCall";

// Add TypeScript declaration for WOW.js
declare global {
  interface Window {
    WOW: any;
  }
}

// Response from the Firebase cloud function
type LiveKitTokenResponse = {
  token: string;
  url: string;
  room: string;
  identity: string;
  expires_in: number;
};

export default function LiveKitAgentPlayground() {
  const [connectionInfo, setConnectionInfo] = useState<{
    token: string;
    url: string;
  } | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [agentState, setAgentState] = useState<AgentState>("disconnected");
  const [visualizerState, setVisualizerState] = useState<AgentState | null>(
    null
  );

  const [room] = useState(new Room());

  // Initialize WOW.js for animations when component mounts
  useEffect(() => {
    // Check if WOW is available
    if (typeof window !== "undefined" && window.WOW) {
      new window.WOW().init();
    } else {
      // Load WOW.js if it's not already loaded
      const script = document.createElement("script");
      script.src = "/js/wow.min.js";
      script.async = true;
      script.onload = () => {
        if (window.WOW) {
          new window.WOW().init();
        }
      };
      document.body.appendChild(script);
    }
  }, []);

  let hasTriggeredAgent = false;
  const onConnectButtonClicked = useCallback(async () => {
    console.log("onConnectButtonClicked");
    setIsConnecting(true);

    try {
      // First request microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      // Clean up the stream after getting permissions
      stream.getTracks().forEach((track) => track.stop());

      // Generate a unique identity for this user session
      const identity = `user_${Math.floor(Math.random() * 100000)}`;
      // Generate a unique room name
      const roomName = `room_${Math.floor(Math.random() * 100000)}`;

      // Call the Firebase cloud function to get a token
      const tokenEndpoint = LIVEKIT_TOKEN_ENDPOINT;

      const response = await fetch(tokenEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identity,
          roomName,
          clientId: "web-app",
          agentName: "eric", // Use 'eric' as the agent name
          metadata: JSON.stringify({
            user_id: "123",
            user_name: "John Doe",
            user_email: "john.doe@example.com",
          }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get LiveKit token");
      }

      const connectionDetailsData: LiveKitTokenResponse = await response.json();

      // // Trigger the Modal agent for this room
      // if (!hasTriggeredAgent) {
      //     console.log("Triggering agent");
      //     await fetch(MODAL_AGENT_ENDPOINT, {
      //         method: "POST",
      //         headers: { "Content-Type": "application/json" },
      //         body: JSON.stringify({
      //             agentName: "eric"
      //         }),
      //     });
      //     hasTriggeredAgent = true;
      //     console.log("Agent triggered");
      // } else {
      //     console.log("Agent already triggered, skipping");
      // }

      // Connect with audio enabled
      await room.connect(
        connectionDetailsData.url,
        connectionDetailsData.token,
        {
          autoSubscribe: true,
          rtcConfig: {
            // Use default audio devices
            bundlePolicy: "balanced",
          },
        }
      );

      // Play connection sound
      const audioBlob = base64ToBlob(startCallBase64, "audio/mp3");
      const audioUrl = URL.createObjectURL(audioBlob);
      const newAudio = new Audio(audioUrl);
      await newAudio.play();

      // Enable microphone after connection
      await room.localParticipant.setMicrophoneEnabled(true);
    } catch (error) {
      console.error("Error connecting:", error);
      alert(
        "Failed to connect. Please ensure you grant microphone permissions and try again."
      );
    } finally {
      setIsConnecting(false);
    }
  }, [room]);

  useEffect(() => {
    room.on(RoomEvent.MediaDevicesError, onDeviceFailure);

    return () => {
      room.off(RoomEvent.MediaDevicesError, onDeviceFailure);
    };
  }, [room]);
  return (
    <>
      <div className="flex h-full w-full">
        {/* <ConversationScenarioSelector /> */}
        <main className="h-full flex-1 flex items-center justify-center bg-[var(--lk-bg)]">
          <div className="w-full max-w-3xl max-h-[80vh] mx-auto px-4">
            <RoomContext.Provider value={room}>
              <div className="lk-room-container grid grid-rows-[2fr_1fr] items-center gap-8">
                <SimpleVoiceAssistant
                  onStateChange={setAgentState}
                  forcedState={visualizerState}
                />
                <ControlBar
                  onConnectButtonClicked={onConnectButtonClicked}
                  agentState={agentState}
                  onStopClick={() => setVisualizerState("listening")}
                  onDisconnectClick={() => setVisualizerState("disconnected")}
                />
                <RoomAudioRenderer />
                <NoAgentNotification state={agentState} />
              </div>
            </RoomContext.Provider>
          </div>
        </main>
      </div>
    </>
  );
}

function SimpleVoiceAssistant(props: {
  onStateChange: (state: AgentState) => void;
  forcedState?: AgentState | null;
}) {
  const { state, audioTrack } = useVoiceAssistant();

  // Display state is either the forced state or the natural state
  const displayState = props.forcedState || state;

  useEffect(() => {
    props.onStateChange(state);
  }, [props, state]);

  return (
    <div className="h-[300px] w-[300px] max-w-[120vw] mx-auto">
      <BarVisualizer
        state={displayState}
        barCount={5}
        trackRef={audioTrack}
        className="agent-visualizer"
        options={{ minHeight: 24 }}
      />
    </div>
  );
}

function ControlBar(props: {
  onConnectButtonClicked: () => void;
  agentState: AgentState;
  onStopClick: () => void;
  onDisconnectClick: () => void;
}) {
  /**
   * Use Krisp background noise reduction when available.
   * Note: This is only available on Scale plan, see {@link https://livekit.io/pricing | LiveKit Pricing} for more details.
   */
  console.log("agentState", props.agentState);
  const krisp = useKrispNoiseFilter();
  useEffect(() => {
    krisp.setNoiseFilterEnabled(true);
  }, []);

  // Add click handlers for the control buttons
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;

      if (target.closest('.lk-agent-control-bar button[aria-label="Stop"]')) {
        props.onStopClick();
      } else if (target.closest(".lk-disconnect-button")) {
        props.onDisconnectClick();
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [props]);

  return (
    <div className="relative h-[100px]">
      <AnimatePresence>
        {props.agentState === "disconnected" && (
          <motion.button
            initial={{ opacity: 0, top: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, top: "-10px" }}
            transition={{
              duration: 1,
              ease: [0.09, 1.04, 0.245, 1.055],
            }}
            className="uppercase absolute left-1/2 -translate-x-1/2 px-4 py-2 bg-white border border-black text-black rounded-md"
            onClick={() => props.onConnectButtonClicked()}
          >
            Start a conversation
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {props.agentState !== "disconnected" && (
          <motion.div
            initial={{ opacity: 0, top: "10px" }}
            animate={{ opacity: 1, top: 0 }}
            exit={{ opacity: 0, top: "-10px" }}
            transition={{
              duration: 0.4,
              ease: [0.09, 1.04, 0.245, 1.055],
            }}
            className="flex h-8 absolute left-1/2 -translate-x-1/2  justify-center"
          >
            <VoiceAssistantControlBar controls={{ leave: false }} />
            <DisconnectButton>
              <CloseIcon />
            </DisconnectButton>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function onDeviceFailure(error?: MediaDeviceFailure | Error) {
  console.error(error);
  alert(
    "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab"
  );
}
